import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { WebConProject, ClientSubcategory, SupportHoursSummary } from '../types';

export function useProjects(filters: { search?: string; page?: number; pageSize?: number } = {}) {
  const params = new URLSearchParams();
  for (const [key, val] of Object.entries(filters)) {
    if (val !== undefined && val !== '') params.set(key, String(val));
  }
  const q = params.toString();

  return useQuery({
    queryKey: ['projects', filters],
    queryFn: () => api.get<WebConProject[]>(`/projects${q ? `?${q}` : ''}`),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

export function useProject(id: string | number) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => api.get<WebConProject>(`/projects/${id}`),
    enabled: !!id,
  });
}

export function useProjectSubcategories(id: string | number) {
  return useQuery({
    queryKey: ['projects', id, 'subcategories'],
    queryFn: () => api.get<ClientSubcategory[]>(`/projects/${id}/subcategories`),
    enabled: !!id,
  });
}

export function useSupportHours(projectId: string | number) {
  return useQuery({
    queryKey: ['support-hours', projectId],
    queryFn: () => api.get<SupportHoursSummary>(`/support-hours/${projectId}`),
    enabled: !!projectId,
  });
}

export function useLogSupportHours() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { contractId: string; ticketId?: string; hours: number; note?: string }) =>
      api.post('/support-hours', data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['support-hours', variables.contractId] });
    },
  });
}
